
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { getSearchSuggestions } from '../services/dataService';
import { getAiSearchResults } from '../services/geminiService';
import { SearchSuggestion, AiSearchResult } from '../types';
import { useCountry } from '../context/CountryContext';
import LoadingSpinner from '../components/LoadingSpinner';
import MarkdownRenderer from '../components/MarkdownRenderer';

const SearchResultCard: React.FC<{ item: SearchSuggestion }> = ({ item }) => {
    const getTypeColor = (type: SearchSuggestion['type']) => {
        switch (type) {
            case 'Bank': return 'bg-blue-100 text-blue-800';
            case 'UPI': return 'bg-green-100 text-green-800';
            case 'Payment App': return 'bg-green-100 text-green-800';
            case 'Card': return 'bg-yellow-100 text-yellow-800';
            case 'Charge': return 'bg-purple-100 text-purple-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };
    
    return (
        <Link to={item.path} className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 ease-in-out transform hover:-translate-y-1 border border-gray-200">
            <div className="flex justify-between items-start">
                <div>
                    <h3 className="font-semibold text-gray-800">{item.name}</h3>
                    {item.details && <p className="text-sm text-gray-500">{item.details}</p>}
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${getTypeColor(item.type)}`}>
                    {item.type}
                </span>
            </div>
        </Link>
    );
};

// Levenshtein distance function for fuzzy matching
function calculateLevenshteinDistance(a: string = '', b: string = ''): number {
    // To handle potential undefined/null inputs from item.code
    const s1 = a || '';
    const s2 = b || '';

    const matrix = Array(s2.length + 1).fill(null).map(() => Array(s1.length + 1).fill(null));

    for (let i = 0; i <= s1.length; i += 1) {
        matrix[0][i] = i;
    }

    for (let j = 0; j <= s2.length; j += 1) {
        matrix[j][0] = j;
    }

    for (let j = 1; j <= s2.length; j += 1) {
        for (let i = 1; i <= s1.length; i += 1) {
            const indicator = s1[i - 1] === s2[j - 1] ? 0 : 1;
            matrix[j][i] = Math.min(
                matrix[j][i - 1] + 1, // deletion
                matrix[j - 1][i] + 1, // insertion
                matrix[j - 1][i - 1] + indicator, // substitution
            );
        }
    }
    return matrix[s2.length][s1.length];
}


const SearchResultsPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { country, availableCountries } = useCountry();
    const countryName = availableCountries.find(c => c.code === country)?.name || 'your region';

    const [query, setQuery] = useState(searchParams.get('q') || '');
    const [dbResults, setDbResults] = useState<SearchSuggestion[]>([]);
    const [aiResponse, setAiResponse] = useState<AiSearchResult | null>(null);
    const [isDbLoading, setIsDbLoading] = useState(true);
    const [isAiLoading, setIsAiLoading] = useState(true);

    const isQuestion = useMemo(() => {
        const q = (searchParams.get('q') || '').toLowerCase().trim();
        if (!q) return false;
        const questionWords = ['what', 'how', 'why', 'explain', 'can', 'do', 'is', 'are', 'which', 'who', 'when'];
        return q.endsWith('?') || q.split(' ').length > 3 || questionWords.includes(q.split(' ')[0]);
    }, [searchParams]);

    useEffect(() => {
        const currentQuery = searchParams.get('q') || '';
        setQuery(currentQuery);

        if (currentQuery) {
            // Reset states for new search
            setDbResults([]);
            setAiResponse(null);

            // Fetch both database and AI results
            const fetchDbResults = async () => {
                setIsDbLoading(true);
                const allItems = await getSearchSuggestions(country);
                const lowerCaseQuery = currentQuery.toLowerCase();

                const scoredAndFiltered = allItems
                    .map(item => {
                        let score = 0;
                        const code = item.code?.toLowerCase();
                        const name = item.name.toLowerCase();
                        const details = item.details?.toLowerCase();

                        // Calculate Levenshtein distance
                        const codeDistance = calculateLevenshteinDistance(code, lowerCaseQuery);
                        const nameDistance = calculateLevenshteinDistance(name, lowerCaseQuery);
                        const fuzzyThreshold = lowerCaseQuery.length > 4 ? 2 : 1;

                        if (code === lowerCaseQuery) {
                            score = 10;
                        } else if (name === lowerCaseQuery) {
                            score = 9;
                        } else if (codeDistance <= fuzzyThreshold) {
                            score = 8 - codeDistance;
                        } else if (nameDistance <= fuzzyThreshold) {
                            score = 6 - nameDistance;
                        } else if (code?.startsWith(lowerCaseQuery)) {
                            score = 5;
                        } else if (name.startsWith(lowerCaseQuery)) {
                            score = 4;
                        } else if (
                            name.includes(lowerCaseQuery) ||
                            (details && details.includes(lowerCaseQuery)) ||
                            (code && code.includes(lowerCaseQuery))
                        ) {
                            score = 1;
                        }
                        return { ...item, score };
                    })
                    .filter(item => item.score > 0)
                    .sort((a, b) => b.score - a.score);

                setDbResults(scoredAndFiltered);
                setIsDbLoading(false);
            };

            const fetchAiResults = async () => {
                setIsAiLoading(true);
                setAiResponse(null);
                try {
                    const result = await getAiSearchResults(currentQuery, countryName);
                    setAiResponse(result);
                } catch (error) {
                    console.error("Error fetching AI search results:", error);
                    setAiResponse({ text: "Sorry, I encountered an error while processing your search.", sources: [] });
                } finally {
                    setIsAiLoading(false);
                }
            };

            fetchDbResults();
            fetchAiResults();
        }
    }, [searchParams, country, countryName]);
    
    const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const newQuery = (event.currentTarget.elements.namedItem('search') as HTMLInputElement).value;
        if (newQuery.trim()) {
            navigate(`/search?q=${encodeURIComponent(newQuery)}`);
        }
    };

    const dbResultsComponent = (
        <div>
            <h2 className="text-2xl font-bold font-poppins mb-4">
                {isQuestion ? 'Related items from our database' : 'From our database'}
            </h2>
            {isDbLoading ? <LoadingSpinner /> : (
                dbResults.length > 0 ? (
                    <div className="space-y-3">
                        {dbResults.map((item, index) => <SearchResultCard key={`${item.path}-${index}`} item={item} />)}
                    </div>
                ) : (
                    <div className="p-6 text-center bg-white rounded-lg border">
                        <p className="text-gray-600">No direct matches found for "{query}" in our database.</p>
                    </div>
                )
            )}
        </div>
    );
    
    const aiResponseComponent = (
        <div>
            <h2 className="text-2xl font-bold font-poppins mb-4">AI-powered answer</h2>
            <div className="p-6 bg-white rounded-lg shadow-sm border border-gray-200 min-h-[200px]">
                {isAiLoading && !aiResponse ? <LoadingSpinner /> : (
                    aiResponse ? (
                        <>
                            <MarkdownRenderer content={aiResponse.text} />
                            {aiResponse.sources && aiResponse.sources.length > 0 && (
                                <div className="mt-6 pt-4 border-t border-gray-200">
                                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Sources:</h3>
                                    <ul className="space-y-2">
                                        {aiResponse.sources.map((source, index) => source.web && (
                                            <li key={index} className="flex items-start text-sm">
                                                <span className="text-gray-500 mr-2 flex-shrink-0">[{index + 1}]</span>
                                                <a
                                                    href={source.web.uri}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:underline break-all"
                                                    title={source.web.uri}
                                                >
                                                    {source.web.title}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    ) : <p className="text-gray-500">The AI assistant could not find an answer.</p>
                )}
            </div>
        </div>
    );
    
    return (
        <div className="bg-gray-50 py-12">
            <div className="container mx-auto px-6">
                <header className="mb-10">
                    <h1 className="text-3xl font-extrabold font-poppins tracking-tight text-gray-900 sm:text-4xl">Search Results</h1>
                     <form onSubmit={handleSearchSubmit} className="mt-4 max-w-2xl">
                        <div className="relative">
                            <input
                                type="search"
                                name="search"
                                defaultValue={query}
                                className="block w-full p-4 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white/90 placeholder-gray-500 focus:ring-2 focus:ring-[#246BFD] focus:outline-none shadow-sm transition"
                                placeholder="Search again..."
                                required
                            />
                            <button type="submit" className="text-white absolute right-2.5 bottom-2.5 bg-[#246BFD] hover:bg-opacity-90 focus:ring-4 focus:outline-none focus:ring-blue-300 font-semibold rounded-lg text-sm px-4 py-2">Search</button>
                        </div>
                    </form>
                </header>

                {isQuestion ? (
                    <div className="space-y-12 max-w-4xl mx-auto">
                        {aiResponseComponent}
                        {(!isDbLoading && dbResults.length > 0) && dbResultsComponent}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                        <div className="lg:col-span-2">
                            {dbResultsComponent}
                        </div>
                        <div className="lg:col-span-3">
                            {aiResponseComponent}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchResultsPage;
